import {DataLayerHelper} from "./tracking/dataLayerHelper";
import {GtmEventFactory} from "./tracking/gtmEventFactory";
import {TRACKING_EVENT_CATEGORY, TRACKING_EVENT_TYPE} from "../tracking";

export class ShopTracking {

    /**
     * @type {string}
     */
    static DATA_ATTRIBUTE_TRACKING_PRODUCT = 'data-tracking-product';

    /**
     * @type {string}
     */
    static DATA_ATTRIBUTE_TRACKING_CART = 'data-tracking-cart';

    /**
     * @type {string}
     */
    static TRANSFER_DATA_TRACKING_STORAGE_KEY = 'transferDataTracking';

    /**
     * @param trackingEventType {string}
     * @param data {Object}
     * @param dataLayerEvent {string}
     * @param dataLayerCategory {string}
     * @returns {void}
     */
    trackCustomEvent(
        trackingEventType,
        data,
        dataLayerEvent,
        dataLayerCategory
    ) {

        const gtmEvent = new GtmEventFactory(data).create(
            {
                [GtmEventFactory.DATA_PARAM_TRACKING_EVENT_TYPE]: trackingEventType,
                [GtmEventFactory.DATA_PARAM_DATA_LAYER_IDENTIFIER]: dataLayerEvent,
            }
        );

        if (gtmEvent.isValid() === false) {
            console.trace('Invalid GTM event');

            return;
        }

        DataLayerHelper.pushEvent(
            dataLayerEvent,
            dataLayerCategory,
            gtmEvent.getEventData()
        );
    }

    /**
     * @param trackingEventType {string}
     * @param data {Object}
     * @param dataLayerEvent {string}
     * @param dataLayerCategory {string}
     * @returns {void}
     */
    trackCustomEventWithTwigExtensionData(
        trackingEventType,
        data,
        dataLayerEvent,
        dataLayerCategory
    ) {

        // Check if data is empty
        if (Object.keys(data).length === 0) {

            console.trace('No data found for tracking event in trackCustomEventWithTwigExtensionData.');
        }

        const gtmEvent = new GtmEventFactory(data).createOnTheFly(
            {
                [GtmEventFactory.DATA_PARAM_TRACKING_EVENT_TYPE]: trackingEventType,
                [GtmEventFactory.DATA_PARAM_DATA_LAYER_IDENTIFIER]: dataLayerEvent,
            }
        );

        /**
         * As mentioned on the call of this method, we need to store the data as
         * we need it on the next step as we don't want to extend server side code.
         */
        if (DataLayerHelper.CHECKOUT_STEP_3_EVENT) {

            ShopTracking.setTrackingDataToStorage(data);
        }

        DataLayerHelper.pushEvent(
            dataLayerEvent,
            dataLayerCategory,
            gtmEvent
        );
    }

    /**
     * @param elementClicked {Element}
     * @param clickEvent {Event}
     * @param dataLayerEvent {string}
     * @returns {void}
     */
    trackClickEvent(
        elementClicked,
        clickEvent,
        dataLayerEvent
    ) {

        const target = clickEvent.target;
        const currentTarget = clickEvent.currentTarget;

        const url = this.tryToEmitUrlForClickTracking(
            target,
            currentTarget
        );

        const label = this.tryToEmitLabelForClickTracking(
            target,
            currentTarget
        )

        const gtmEvent = new GtmEventFactory({
            [GtmEventFactory.DATA_PARAM_CLICK_EVENT_URL]: url,
            [GtmEventFactory.DATA_PARAM_CLICK_EVENT_LABEL]: label
        }).createOnTheFly(
            {
                [GtmEventFactory.DATA_PARAM_TRACKING_EVENT_TYPE]: TRACKING_EVENT_TYPE.CLICKLISTENERSHOP,
                [GtmEventFactory.DATA_PARAM_DATA_LAYER_IDENTIFIER]: dataLayerEvent,
            }
        );

        DataLayerHelper.pushEvent(
            dataLayerEvent,
            TRACKING_EVENT_CATEGORY.CLICK,
            gtmEvent
        );
    }

    /**
     * @param target {Element}
     * @param currentTarget {Element}
     * @returns {*}
     */
    tryToEmitUrlForClickTracking(
        target,
        currentTarget
    ) {
        const targetElementWithHref = target.closest("[href]");
        const currentTargetElementWithHref = currentTarget.closest("[href]");

        if (targetElementWithHref) {

            return targetElementWithHref.getAttribute('href');
        }

        if (currentTargetElementWithHref) {

            return currentTargetElementWithHref.getAttribute
        }

        const targetElementWithSrc = target.closest("[src]");
        const currentTargetElementWithSrc = currentTarget.closest("[src]");

        if (targetElementWithSrc) {

            return targetElementWithSrc.getAttribute('src');
        }

        if (currentTargetElementWithSrc) {

            return currentTargetElementWithSrc.getAttribute
        }

        const targetElementWithAction = target.closest("[action]");
        const currentTargetElementWithAction = currentTarget.closest("[action]");

        if (targetElementWithAction) {

            return targetElementWithAction.getAttribute('action');
        }

        if (currentTargetElementWithAction) {

            return currentTargetElementWithAction.getAttribute('action');
        }
    }

    /**
     * @param target {Element}
     * @param currentTarget {Element}
     * @returns {*|string}
     */
    tryToEmitLabelForClickTracking(
        target,
        currentTarget
    ) {
        const elementWithLabel = target.closest("[data-label]");
        const currentTargetElementWithLabel = currentTarget.closest("[data-label]");

        if (elementWithLabel) {
            return elementWithLabel.getAttribute("data-label");
        }

        if (currentTargetElementWithLabel) {
            return currentTargetElementWithLabel.getAttribute("data-label");
        }

        if (target.textContent && target.textContent.trim() !== "") {
            return target.textContent.trim();
        }

        if (currentTarget.textContent && currentTarget.textContent.trim() !== "") {
            return currentTarget.textContent.trim();
        }

        if (target.classList.length > 0) {
            return Array.from(target.classList).join(" ");
        }

        if (currentTarget.classList.length > 0) {
            return Array.from(currentTarget.classList).join(" ");
        }

        // Nothing found!
        return "unknown";
    }

    /**
     * @param elementSubmitted {Element}
     * @param submitEvent {Event}
     * @param dataLayerEvent {string}
     * @returns {void}
     */
    trackFormSubmitEvent(
        elementSubmitted,
        submitEvent,
        dataLayerEvent = ''
    ) {
        const form = elementSubmitted.closest('form');
        const formData = new FormData(form)

        if (!formData) {

            return;
        }

        // Search on shop page.
        if (formData.has('query') ||
            formData.has('search-fin')
        ) {
            this.handleSearchTracking(
                elementSubmitted,
                formData
            );
        }

        // Apply coupon.
        if (formData.has('checkout_voucher_form[voucher]')) {

            this.handleCouponTracking(
                elementSubmitted,
                formData
            );
        }
    }

    /**
     * @param elementSubmitted {Element}
     * @param formData {FormData}
     * @returns {void}
     */
    handleSearchTracking(
        elementSubmitted,
        formData
    ) {
        let dataLayerEvent = 'search';
        let searchQuery = '';

        // "Normal" spare part search.
        if (formData.has('query')) {
            dataLayerEvent = DataLayerHelper.SEARCH_EVENT;
            searchQuery = formData.get('query');
        }

        // VIN search.
        if (formData.has('search-fin')) {
            dataLayerEvent = DataLayerHelper.VIN_SEARCH_EVENT
            searchQuery = formData.get('search-fin');
        }

        const gtmEvent = new GtmEventFactory({
            [GtmEventFactory.DATA_PARAM_SEARCH_QUERY]: searchQuery,
        }).createOnTheFly(
            {
                [GtmEventFactory.DATA_PARAM_TRACKING_EVENT_TYPE]: TRACKING_EVENT_TYPE.SUBMITLISTENERSHOP,
                [GtmEventFactory.DATA_PARAM_DATA_LAYER_IDENTIFIER]: dataLayerEvent,
            }
        );

        DataLayerHelper.pushEvent(
            dataLayerEvent,
            TRACKING_EVENT_CATEGORY.SEARCH,
            gtmEvent
        );
    }

    /**
     * @param elementSubmitted {Element}
     * @param formData {FormData}
     * @returns {void}
     */
    handleCouponTracking(
        elementSubmitted,
        formData
    ) {
        let dataLayerEvent = DataLayerHelper.APPLY_COUPON_EVENT;
        let couponCode = '';

        if (formData.has('checkout_voucher_form[voucher]')) {

            couponCode = formData.get('checkout_voucher_form[voucher]')
        }

        const gtmEvent = new GtmEventFactory({
            [GtmEventFactory.DATA_PARAM_COUPON_CODE]: couponCode,
        }).createOnTheFly(
            {
                [GtmEventFactory.DATA_PARAM_TRACKING_EVENT_TYPE]: TRACKING_EVENT_TYPE.SUBMITLISTENERSHOP,
                [GtmEventFactory.DATA_PARAM_DATA_LAYER_IDENTIFIER]: dataLayerEvent,
            }
        );

        DataLayerHelper.pushEvent(
            dataLayerEvent,
            TRACKING_EVENT_CATEGORY.SELECT,
            gtmEvent
        );
    }

    /**
     * @param element {Element}
     * @param event {Event}
     * @param dataLayerEvent {string}
     * @param trackingDataAttribute {string}
     */
    trackClickEventBasedOnTrackingData(
        element,
        event,
        dataLayerEvent,
        trackingDataAttribute = ShopTracking.DATA_ATTRIBUTE_TRACKING_CART
    ) {

        const trackingData = ShopTracking.getClosestTrackingData(
            element,
            trackingDataAttribute
        );

        if (trackingData === null) {

            return;
        }

        const trackingDataJson = JSON.parse(trackingData);

        if (trackingDataJson === null) {

            return;
        }

        // Get shipping information. Not happy with this solution.
        if (dataLayerEvent === DataLayerHelper.ADD_SHIPPING_INFO) {

            if ('detail' in event) {

                if ('shipping_type' in event.detail) {

                    trackingDataJson['shipping'] = event.detail['shipping_type'] ?? '';
                }
            }
        }

        // Get payment information. Not happy with this solution.
        if (dataLayerEvent === DataLayerHelper.ADD_PAYMENT_INFO) {
            const elementLabel = element.getAttribute('data-tracking-payment-type');

            if (elementLabel) {
                trackingDataJson['payment'] = elementLabel;
            }
        }

        const gtmEvent = new GtmEventFactory(trackingDataJson).create(
            {
                [GtmEventFactory.DATA_PARAM_TRACKING_EVENT_TYPE]: TRACKING_EVENT_TYPE.CLICKLISTENERSHOP,
                [GtmEventFactory.DATA_PARAM_DATA_LAYER_IDENTIFIER]: dataLayerEvent,
            }
        );

        DataLayerHelper.pushEvent(
            dataLayerEvent,
            TRACKING_EVENT_CATEGORY.CLICK,
            gtmEvent.getEventData()
        );
    }

    /**
     * @param elementToStartSearching {Element}
     * @param dataAttributeName {string}
     * @returns {string|null}
     */
    static getClosestTrackingData(
        elementToStartSearching,
        dataAttributeName
    ) {

        if (elementToStartSearching === null) {

            return null;
        }

        let elementDataValue = '';
        let elementWithData = null;

        // First check if the element has the data attribute
        if (elementToStartSearching.hasAttribute(dataAttributeName)) {

            elementDataValue = elementToStartSearching.getAttribute(dataAttributeName);
            elementWithData = elementToStartSearching;
        }

        // Check on closest element
        if (elementDataValue === '') {
            const dataSelector = `[${dataAttributeName}]`;

            elementWithData = elementToStartSearching.closest(dataSelector);

            if (elementWithData === null) {

                return null;
            }

            // First get specific data from the element
            elementDataValue = elementWithData?.getAttribute(dataAttributeName);
        }

        // Retrieve the data from the tracking attribute and decode it
        const dataFromTrackingAttribute = Buffer
            .from(elementDataValue, 'base64')
            .toString('utf-8');

        if (elementWithData !== undefined &&
            dataFromTrackingAttribute !== ''
        ) {

            return dataFromTrackingAttribute;
        }

        return null;
    }

    /**
     * @param elementClicked {Element}
     * @param clickEvent {Event}
     * @param dataLayerEvent {string}
     */
    trackCustomizedClickEvent(
        elementClicked,
        clickEvent,
        dataLayerEvent
    ) {

        const trackingData = {};

        if (dataLayerEvent === DataLayerHelper.CHECKOUT_STEP_1_EVENT) {
            trackingData['step'] = 1;

            let checkoutType = elementClicked.getAttribute('data-tracking-checkout-step1-user');
            if (checkoutType !== null) {
                trackingData['checkout_type'] = 'user';
            }

            checkoutType = elementClicked.getAttribute('data-tracking-checkout-step1-guest');
            if (checkoutType !== null) {
                trackingData['checkout_type'] = 'guest';
            }

            checkoutType = elementClicked.getAttribute('data-tracking-checkout-step1-new');
            if (checkoutType !== null) {
                trackingData['checkout_type'] = 'new';
            }
        }

        if (dataLayerEvent === DataLayerHelper.CHECKOUT_STEP_2_EVENT) {
            trackingData['step'] = 2;
        }

        if (dataLayerEvent === DataLayerHelper.EDIT_CONFIRM_ADDRESS_EVENT) {
            let openElement = elementClicked.getAttribute('data-tracking-open');

            if (openElement !== null) {
                let openElementArray = openElement.split('-');

                trackingData['address_type'] = openElementArray.slice(0, 2).join('_');
            }
        }

        DataLayerHelper.pushEvent(
            dataLayerEvent,
            TRACKING_EVENT_CATEGORY.CLICK,
            trackingData
        );
    }

    /**
     * @param formElement {Element}
     * @param formName {string}
     * @param errorMessages {Array}
     * @returns {void}
     */
    trackFormErrorEvent(
        formElement,
        formName,
        errorMessages
    ) {

        const trackingData = {
            form: formName,
            errors: errorMessages
        };

        DataLayerHelper.pushEvent(
            DataLayerHelper.FORM_ERROR,
            TRACKING_EVENT_CATEGORY.FORM,
            trackingData
        );
    }

    /**
     * @param element
     * @param errorMessage
     * @returns {void}
     */
    trackGlobalErrorEvent(
        element,
        errorMessage
    ) {
        const trackingData = {
            element: 'unknown element',
            error: errorMessage,
        };

        // try to select lang from html tag
        if (document.documentElement.hasAttribute('lang')) {
            trackingData.language = document.documentElement.getAttribute('lang');
        }

        // Get url
        trackingData[GtmEventFactory.DATA_PARAM_CLICK_EVENT_URL] = window.location.href;

        if (element.hasAttribute('name')) {
            trackingData.element = element.getAttribute('name');
        }

        if (element.hasAttribute('id') &&
            trackingData.element === 'unknown element'
        ) {
            trackingData.element = element.getAttribute('id');
        }

        if (element.hasAttribute('class') &&
            trackingData.element === 'unknown element'
        ) {
            trackingData.element = element.getAttribute('class');
        }

        DataLayerHelper.pushEvent(
            DataLayerHelper.GLOBAL_ERROR,
            TRACKING_EVENT_CATEGORY.ERROR,
            trackingData
        );
    }

    /**
     * Simple static method to return a "description" of
     * validity state of a curtain kind.
     *
     * @param validity {ValidityState}
     * @returns {string}
     */
    static getErrorDescription(validity) {
        if (validity.valueMissing) {
            return 'required';
        } else if (validity.typeMismatch) {
            return 'typeMismatch';
        } else if (validity.patternMismatch) {
            return 'patternMismatch';
        } else if (validity.tooShort) {
            return 'tooShort';
        } else if (validity.tooLong) {
            return 'tooLong';
        } else if (validity.rangeUnderflow) {
            return 'rangeUnderflow';
        } else if (validity.rangeOverflow) {
            return 'rangeOverflow';
        } else if (validity.stepMismatch) {
            return 'stepMismatch';
        } else if (validity.customError) {
            return 'customError';
        } else {
            return 'invalid';
        }
    }

    /**
     * @param data {Object}
     * @returns {void}
     */
    static setTrackingDataToStorage(data) {

        try {

            localStorage.setItem(
                ShopTracking.TRANSFER_DATA_TRACKING_STORAGE_KEY,
                JSON.stringify(data)
            );

        } catch (error) {

            console.error(
                'Error on setting transfer data to storage.',
                error
            );
        }
    }

    /**
     * @returns {Object}
     */
    static getTrackingDataFromStorage() {
        let transferEntry = null;

        try {

            transferEntry = JSON.parse(
                localStorage.getItem(ShopTracking.TRANSFER_DATA_TRACKING_STORAGE_KEY)
            );

        } catch (error) {

            console.error(
                'Error on getting transfer data back from storage.',
                error
            );
        }

        return transferEntry;
    }

    /**
     * @param trackingEventType
     * @param data
     * @param dataLayerEvent
     * @param dataLayerCategory
     * @returns {void}
     */
    trackCustomEventUsingLocalStorageData(
        trackingEventType,
        data,
        dataLayerEvent,
        dataLayerCategory
    ) {

        const transferData = ShopTracking.getTrackingDataFromStorage();

        if (transferData === null) {
            console.trace('No transfer data found for purchase event. Can not track event.');

            return;
        }

        if (dataLayerEvent === DataLayerHelper.PURCHASE_EVENT) {
            transferData['transaction_id'] = data['order'] ?? 'time_'.Math.floor(Date.now() / 1000);
        }

        DataLayerHelper.pushEvent(
            dataLayerEvent,
            dataLayerCategory,
            transferData
        );
    }

    /**
     * @param element
     * @param selectedValue
     * @returns {void}
     */
    trackCheckoutCountryMismatch(
        element,
        selectedValue,
    ) {
        const dataTrackingElement = document.querySelector('.checkout-aside-container');

        if (dataTrackingElement === null) {
            console.trace('No checkout-aside-container found. Can not track event.');
        }

        const cartCountry = dataTrackingElement.getAttribute('data-tracking-cart-country');

        const trackingData = {
            origin_country: cartCountry,
            selected_country: selectedValue,
            match: cartCountry === selectedValue,
        }

        DataLayerHelper.pushEvent(
            DataLayerHelper.COUNTRY_VALIDATION_EVENT,
            TRACKING_EVENT_CATEGORY.SELECT,
            trackingData
        );
    }
}